import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { timeout, catchError, throttleTime } from "rxjs/operators";
import useToggle from "../../../hooks/useToggle";
import PHASE_STATUS from "@common/constants/phaseStatus";
import {
  SESSION_STATES,
  SESSION_TYPES,
} from "@common/constants/sessionConstants";
import PROCESS_TYPES from "@common/constants/processTypes";
import Panel from "../Panel/Panel";
import PanelHeader from "../PanelHeader/PanelHeader";
import ChartManager from "../ChartManager/ChartManager";
import mqttService from "../../../mqttService";
import BrewingOptions from "../ChartManager/Options/brewingOptions";
import FermentingOptions from "../ChartManager/Options/fermentationOptions";
import ServingOptions from "../ChartManager/Options/servingOptions";
import CleaningOptions from "../ChartManager/Options/cleaningOptions";
import LoadingSession from "../LoadingSession/LoadingSession";
import { Button } from "react-bootstrap";
import * as sessionService from "@brewery/services/sessionService";
import { routes } from "@common/constants/urls";
import { getPhaseTimeDetails } from "../../../../util/timelineHelper";

/**
 * Manages the set up of the MQTT communication regarding frequent logging
 * and takes care of the panel state (which panel should open automatically)
 */
const PanelManager = ({ session, mqtt, onStatusChange, routeTo }) => {
  const [brewingOpen, setBrewingOpen, toggleBrewingOpen] = useToggle();
  const [fermentingOpen, setfermentingOpen, toggleFermentingOpen] = useToggle();
  const [servingOpen, setservingOpen, toggleServingOpen] = useToggle();
  const [activeProcess, setActiveProcess] = useState(null);
  const [lastLog, setLastLog] = useState(null);
  const [waitingForLog, setWaitingForLog] = useState(true);
  const [timeoutExpired, setTimeoutExpired] = useState(false);
  const [forceEndNotice, setForceEndNotice] = useState(null);
  const [confirmingForceEnd, setConfirmingForceEnd] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const activeProcessRef = useRef(activeProcess);
  activeProcessRef.current = activeProcess;

  useEffect(() => {
    let timer;
    if (waitingForLog) {
      setTimeoutExpired(false);
      timer = setTimeout(() => {
        setTimeoutExpired(true);
      }, 15000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [waitingForLog]);

  const leaveToDashboard = () => {
    if (routeTo) {
      routeTo(routes.brewery);
    } else {
      window.location.href = "/brewery";
    }
  };

  // request.js rejects with the parsed-body PROMISE and no status code, so the
  // body is all we can branch on -- which is why the backend puts can_override
  // in it.
  const readErrorBody = async (err) => {
    if (err && typeof err.then === "function") {
      try {
        return await err;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const handleForceEndSession = async () => {
    if (!session || isEndingSession) return;

    setIsEndingSession(true);
    try {
      const res = await sessionService.deleteSession(session.id, true);
      // A device parked at CIP-done is NOT aborted by a plain force-delete: the
      // backend resumes the teardown instead and deliberately keeps the session
      // ACTIVE, so leaving for the dashboard here would claim a success that did
      // not happen.
      if (res && res.status === "resuming-teardown") {
        setConfirmingForceEnd(false);
        setForceEndNotice(
          "The machine is still finishing its cleaning cycle, so the session was " +
            "kept and the teardown resumed. If it stays stuck, end it anyway.",
        );
        return;
      }
      leaveToDashboard();
    } catch (err) {
      setConfirmingForceEnd(false);
      const body = await readErrorBody(err);
      // 409 + can_override: nothing was published (device offline), the session
      // is untouched, and only the explicit override can clear it. Previously
      // this branch redirected regardless and the failure went unseen.
      if (body && body.can_override) {
        setForceEndNotice(
          (body.detail ||
            "The machine could not be reached while it is mid-cleaning.") +
            " You can end the session anyway.",
        );
        return;
      }
      setForceEndNotice(
        "Ending the session failed. Please try again, or contact MiniBrew support.",
      );
    } finally {
      setIsEndingSession(false);
    }
  };

  // The operator override: abort the session even though the device is still
  // parked at CIP-done. The backend logs the divergence; the machine will still
  // need its teardown finished by hand.
  const handleForceEndAnyway = async () => {
    if (!session || isEndingSession) return;
    setIsEndingSession(true);
    try {
      await sessionService.deleteSession(session.id, true, true);
      leaveToDashboard();
    } catch (err) {
      setForceEndNotice(
        "Ending the session failed. Please try again, or contact MiniBrew support.",
      );
    } finally {
      setIsEndingSession(false);
    }
  };

  useEffect(() => {
    let mqttSubscriptions = [];
    //if (session !== null && activeProcess !== session.device.process_type) {
    if (session !== null) {
      const session_status = session.status;
      const process_type = session.device.process_type;
      updatePanelState(process_type);
      if (session_status == SESSION_STATES.STATUS_ACTIVE) {
        mqttSubscriptions = setUpMqttSubscriptions();
      } else if (session_status >= SESSION_STATES.STATUS_FINISHED) {
        setBrewingOpen(false);
        setfermentingOpen(false);
        setservingOpen(false);
        setWaitingForLog(false);
        setActiveProcess(PROCESS_TYPES.PROC_IDLE);
      }
    }
    return () => {
      if (mqttService.isConnected() && session) {
        const { uuid } = session.device;
        stopLogs(uuid, mqtt.clientUUID);
        mqttService.unsubscribeFrequentLog(uuid);
        mqttService.unsubscribeFrequentLogResp(uuid);
        mqttSubscriptions.forEach((subscription) => subscription.unsubscribe());
      }
    };
  }, [session]);

  useEffect(() => {
    let subscription;
    if (session) {
      subscription = mqttService.frequentLog$
        .pipe(throttleTime(1000))
        .subscribe((frequentlog) => {
          if (frequentlog.session === 0 || frequentlog.session === session.id) {
            const log = frequentlog.logsList?.[0];
            if (log) {
              setLastLog(log);
              if (
                log.currentStatus &&
                log.currentStatus.procType !== undefined
              ) {
                updatePanelState(log.currentStatus.procType);
              }
            }
          }
        });
    }
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [session]);

  const updatePanelState = (process_type) => {
    if (activeProcessRef.current !== process_type) {
      if (process_type == PROCESS_TYPES.PROC_BREWING) {
        setBrewingOpen(true);
        setfermentingOpen(false);
        setservingOpen(false);
      } else if (process_type == PROCESS_TYPES.PROC_FERMENTATION) {
        setBrewingOpen(false);
        setfermentingOpen(true);
        setservingOpen(false);
      } else if (process_type == PROCESS_TYPES.PROC_SERVING) {
        setBrewingOpen(false);
        setfermentingOpen(false);
        setservingOpen(true);
      }
      setActiveProcess(process_type);
    }
  };
  const setUpMqttSubscriptions = () => {
    const { uuid } = session.device;

    let frequentlogWithTimeout$ = mqttService.frequentLog$.pipe(
      timeout(5000),
      catchError(() => {
        if (mqttService.isConnected()) {
          mqttService.subscribeFrequentLog(uuid);
          mqttService.subscribeFrequentLogResp(uuid);
          mqttService.publishRequestFrequentLogs(uuid, mqtt.clientUUID);
        }
        return frequentlogWithTimeout$;
      }),
    );

    const frequentLogSubscription = frequentlogWithTimeout$.subscribe((fl) => {
      const process_type = fl?.logsList?.[0]?.currentStatus?.procType;
      if (
        process_type !== undefined &&
        process_type !== PROCESS_TYPES.PROC_IDLE
      ) {
        updatePanelState(process_type);
        setWaitingForLog(false);
        frequentLogSubscription.unsubscribe();
      }
    });

    return [frequentLogSubscription];
  };

  const stopLogs = () => {
    mqttService.publishStopFrequentLogs(session.device.uuid, mqtt.clientUUID);
  };

  const brewingActive = activeProcess == PROCESS_TYPES.PROC_BREWING;
  const fermentingActive = activeProcess == PROCESS_TYPES.PROC_FERMENTATION;
  const servingActive = activeProcess == PROCESS_TYPES.PROC_SERVING;
  const cleaningActive = activeProcess == PROCESS_TYPES.PROC_CLEAN_MINIBREW;
  const acidCleaningActive =
    activeProcess == PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW;

  const getTimeRemaining = () => {
    return lastLog ? lastLog.processTimeRemaining : null;
  };
  const sessionCleaning =
    session &&
    session.session_type === SESSION_TYPES.SESSION_TYPE_CLEAN_MINIBREW;
  const sessionAcidCleaning =
    session &&
    session.session_type === SESSION_TYPES.SESSION_ACID_CLEAN_MINIBREW;
  const sessionDone = session && session.status > SESSION_STATES.STATUS_ACTIVE;

  const brewingDetails = getPhaseTimeDetails(session, PHASE_STATUS.BREWING);
  const fermentingDetails = getPhaseTimeDetails(
    session,
    PHASE_STATUS.FERMENTATION,
  );
  const servingDetails = getPhaseTimeDetails(session, PHASE_STATUS.SERVING);
  const acidCleaningDetails = sessionAcidCleaning
    ? getPhaseTimeDetails(session, PHASE_STATUS.CLEANING)
    : null;
  const cleaningDetails = sessionCleaning
    ? getPhaseTimeDetails(session, PHASE_STATUS.CLEANING)
    : null;

  // Bubble up lastLog and activeProcess to parent for ControlSession rendering
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({ lastLog, activeProcess, waitingForLog });
    }
  }, [lastLog, activeProcess, waitingForLog]);

  return (
    <div>
      {session && waitingForLog && !timeoutExpired && <LoadingSession />}
      {session && waitingForLog && timeoutExpired && (
        <div className="offline-diagnostic-card">
          <h3
            style={{
              margin: "0 0 20px",
              color: "#333",
              fontSize: "22px",
              fontFamily: "Gotham-Bold, sans-serif",
              textAlign: "center",
            }}
          >
            Device Offline or Unresponsive
          </h3>
          <p
            style={{
              margin: "0 0 25px",
              color: "#555",
              fontSize: "14px",
              lineHeight: "1.6",
              textAlign: "center",
            }}
          >
            We haven&apos;t received recent logs from your MiniBrew. Please
            verify that the device is turned on and connected to Wi-Fi.
          </p>

          <div
            style={{
              background: "#f9f9f9",
              padding: "20px",
              borderRadius: "6px",
              margin: "0 0 25px",
              borderLeft: "4px solid #f0ad4e",
            }}
          >
            <h4
              style={{
                margin: "0 0 12px",
                fontSize: "14px",
                color: "#333",
                fontWeight: "bold",
              }}
            >
              What are your options?
            </h4>

            <div style={{ margin: "0 0 15px" }}>
              <strong style={{ color: "#333", fontSize: "13px" }}>
                1. Go back to Dashboard
              </strong>
              <p
                style={{
                  margin: "3px 0 0",
                  color: "#666",
                  fontSize: "12px",
                  lineHeight: "1.5",
                }}
              >
                Keep the session active in the background. Use this option to
                wait for the connection to recover or if you want to inspect
                other devices.
              </p>
            </div>

            <div>
              <strong style={{ color: "#d9534f", fontSize: "13px" }}>
                2. Force End Session
              </strong>
              <p
                style={{
                  margin: "3px 0 0",
                  color: "#666",
                  fontSize: "12px",
                  lineHeight: "1.5",
                }}
              >
                Cancel and abort the session immediately.{" "}
                <span style={{ color: "#c9302c", fontWeight: "bold" }}>
                  Warning:
                </span>{" "}
                If the device is in the middle of a brew, this terminates the
                session permanently and it cannot be resumed.
              </p>
            </div>
          </div>

          <p
            style={{
              margin: "0 0 30px",
              color: "#777",
              fontSize: "13px",
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Unsure what to do? Please contact MiniBrew support for guidance.
          </p>

          {forceEndNotice ? (
            <div>
              <p
                style={{
                  margin: "0 0 15px",
                  color: "#d9534f",
                  fontSize: "14px",
                  textAlign: "center",
                  lineHeight: "1.5",
                }}
              >
                {forceEndNotice}
              </p>
              <div className="offline-diagnostic-actions">
                <Button
                  onClick={() => {
                    setForceEndNotice(null);
                    setConfirmingForceEnd(false);
                  }}
                  disabled={isEndingSession}
                  style={{ padding: "8px 18px", fontSize: "14px" }}
                >
                  Cancel
                </Button>
                <Button
                  bsStyle="danger"
                  onClick={handleForceEndAnyway}
                  disabled={isEndingSession}
                  style={{ padding: "8px 18px", fontSize: "14px" }}
                >
                  {isEndingSession ? "Ending Session..." : "End Session Anyway"}
                </Button>
              </div>
            </div>
          ) : confirmingForceEnd ? (
            <div>
              <p
                style={{
                  margin: "0 0 15px",
                  color: "#d9534f",
                  fontSize: "14px",
                  textAlign: "center",
                  lineHeight: "1.5",
                  fontWeight: "bold",
                }}
              >
                Are you sure you want to terminate this session? This cannot be
                undone.
              </p>
              <div className="offline-diagnostic-actions">
                <Button
                  onClick={() => setConfirmingForceEnd(false)}
                  disabled={isEndingSession}
                  style={{ padding: "8px 18px", fontSize: "14px" }}
                >
                  Cancel
                </Button>
                <Button
                  bsStyle="danger"
                  onClick={handleForceEndSession}
                  disabled={isEndingSession}
                  style={{ padding: "8px 18px", fontSize: "14px" }}
                >
                  {isEndingSession ? "Ending Session..." : "Yes, End Session"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="offline-diagnostic-actions">
              <Button
                onClick={() =>
                  routeTo
                    ? routeTo(routes.brewery)
                    : (window.location.href = "/brewery")
                }
                style={{ padding: "8px 18px", fontSize: "14px" }}
              >
                Go back to Dashboard
              </Button>
              <Button
                bsStyle="danger"
                onClick={() => setConfirmingForceEnd(true)}
                style={{ padding: "8px 18px", fontSize: "14px" }}
              >
                Force End Session
              </Button>
            </div>
          )}
        </div>
      )}
      {!waitingForLog && (
        <div>
          {session && !sessionCleaning && !sessionAcidCleaning && (
            <div>
              <Panel
                header={
                  <PanelHeader
                    number={1}
                    stageName="Brewing"
                    deviceName={session.device.custom_name}
                    timeRemaining={brewingActive && getTimeRemaining()}
                    active={brewingActive}
                    done={
                      brewingDetails.status === "Completed" ||
                      (session.status === SESSION_STATES.STATUS_FINISHED
                        ? session.session_type ===
                          SESSION_TYPES.SESSION_TYPE_BREW_FERM
                        : activeProcess > PROCESS_TYPES.PROC_BREWING)
                    }
                    sessionDone={sessionDone}
                    timeRange={brewingDetails.timeRangeFormatted}
                    status={brewingDetails.status}
                    duration={brewingDetails.durationFormatted}
                  />
                }
                handleToggle={toggleBrewingOpen}
                open={brewingOpen}
                active={brewingActive}
                disabled={
                  sessionDone
                    ? session.session_type !==
                      SESSION_TYPES.SESSION_TYPE_BREW_FERM
                    : activeProcess < PROCESS_TYPES.PROC_BREWING
                }
                sessionDone={sessionDone}
              >
                <ChartManager
                  active={brewingActive}
                  session={session}
                  options={BrewingOptions()}
                  type={PHASE_STATUS.BREWING}
                  open={brewingOpen}
                  mqtt={mqtt}
                />
              </Panel>
              <Panel
                header={
                  <PanelHeader
                    number={2}
                    stageName="Fermenting"
                    deviceName={session.device.custom_name}
                    timeRemaining={fermentingActive && getTimeRemaining()}
                    active={fermentingActive}
                    done={
                      fermentingDetails.status === "Completed" ||
                      (session.status === SESSION_STATES.STATUS_FINISHED
                        ? session.session_type ===
                            SESSION_TYPES.SESSION_TYPE_BREW_FERM ||
                          session.session_type ===
                            SESSION_TYPES.SESSION_TYPE_FERMENTATION
                        : activeProcess > PROCESS_TYPES.PROC_FERMENTATION)
                    }
                    sessionDone={sessionDone}
                    timeRange={fermentingDetails.timeRangeFormatted}
                    status={fermentingDetails.status}
                    duration={fermentingDetails.durationFormatted}
                  />
                }
                handleToggle={toggleFermentingOpen}
                open={fermentingOpen}
                active={fermentingActive}
                disabled={
                  sessionDone
                    ? session.session_type !==
                        SESSION_TYPES.SESSION_TYPE_BREW_FERM &&
                      session.session_type !==
                        SESSION_TYPES.SESSION_TYPE_FERMENTATION
                    : activeProcess < PROCESS_TYPES.PROC_FERMENTATION
                }
                sessionDone={sessionDone}
              >
                <ChartManager
                  active={fermentingActive}
                  session={session}
                  options={FermentingOptions()}
                  type={PHASE_STATUS.FERMENTATION}
                  open={fermentingOpen}
                  mqtt={mqtt}
                />
              </Panel>
              <Panel
                header={
                  <PanelHeader
                    number={3}
                    stageName="Serving"
                    deviceName={session.device.custom_name}
                    timeRemaining={servingActive && getTimeRemaining()}
                    active={servingActive}
                    done={
                      servingDetails.status === "Completed" ||
                      (session.status === SESSION_STATES.STATUS_FINISHED
                        ? session.session_type ===
                            SESSION_TYPES.SESSION_TYPE_BREW_FERM ||
                          session.session_type ===
                            SESSION_TYPES.SESSION_TYPE_FERMENTATION
                        : false)
                    }
                    sessionDone={sessionDone}
                    timeRange={servingDetails.timeRangeFormatted}
                    status={servingDetails.status}
                    duration={servingDetails.durationFormatted}
                  />
                }
                handleToggle={toggleServingOpen}
                open={servingOpen}
                active={servingActive}
                disabled={
                  sessionDone
                    ? session.session_type !==
                        SESSION_TYPES.SESSION_TYPE_BREW_FERM &&
                      session.session_type !==
                        SESSION_TYPES.SESSION_TYPE_FERMENTATION
                    : activeProcess < PROCESS_TYPES.PROC_SERVING
                }
                sessionDone={sessionDone}
              >
                <ChartManager
                  active={servingActive}
                  session={session}
                  options={ServingOptions()}
                  type={PHASE_STATUS.SERVING}
                  open={servingOpen}
                  mqtt={mqtt}
                />
              </Panel>
            </div>
          )}
          {sessionAcidCleaning && (
            <div>
              <Panel
                header={
                  <PanelHeader
                    number={1}
                    stageName="Acid Cleaning"
                    deviceName={session.device.custom_name}
                    timeRemaining={getTimeRemaining()}
                    active={acidCleaningActive}
                    timeRange={acidCleaningDetails?.timeRangeFormatted}
                    status={acidCleaningDetails?.status}
                    duration={acidCleaningDetails?.durationFormatted}
                  />
                }
                handleToggle={() => {}}
                open={true}
                active={acidCleaningActive}
              >
                <ChartManager
                  active={acidCleaningActive}
                  session={session}
                  options={CleaningOptions()}
                  type={PHASE_STATUS.CLEANING}
                  open={true}
                  mqtt={mqtt}
                />
              </Panel>
            </div>
          )}
          {sessionCleaning && (
            <div>
              <Panel
                header={
                  <PanelHeader
                    number={1}
                    stageName="Cleaning"
                    deviceName={session.device.custom_name}
                    timeRemaining={getTimeRemaining()}
                    active={cleaningActive}
                    timeRange={cleaningDetails?.timeRangeFormatted}
                    status={cleaningDetails?.status}
                    duration={cleaningDetails?.durationFormatted}
                  />
                }
                handleToggle={() => {}}
                open={true}
                active={cleaningActive}
              >
                <ChartManager
                  active={cleaningActive}
                  session={session}
                  options={CleaningOptions()}
                  type={PHASE_STATUS.CLEANING}
                  open={true}
                  mqtt={mqtt}
                />
              </Panel>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ mqtt }) => ({
  mqtt,
});

export default connect(mapStateToProps, null)(PanelManager);
